import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Award,
  Sparkles,
  BarChart3,
  RefreshCw,
  Settings,
  TrendingUp,
  Users,
  Target,
  Brain,
  CheckCircle,
  Star,
  PieChart,
  Download,
  Share2,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { interviewActions } from "../../../Redux/ActionCreator.js";
import CircularProgress from "../common/CircularProgress";
import PerformanceChart from "../common/PerformanceChart";
import StatCard from "../common/StatCard";
import { ApiService } from "../api.service";
import { ToastContainer, toast } from "react-toastify";

const api = new ApiService();

const Sp_ResultsPage = () => {
  const dispatch = useDispatch();
  const { questions, analytics, ui, interviewConfig, interviewData } =
    useSelector((state) => state.interview);

  const [overallAnalytics, setOverallAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { darkMode } = ui.settings;
  const { allQuestionAnalytics, overallScore, completedQuestions } = analytics;

  const theme = {
    bg: darkMode
      ? "bg-gray-900"
      : "bg-gradient-to-br from-blue-50 to-indigo-100",
    card: darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200",
    text: darkMode ? "text-gray-100" : "text-gray-900",
    textSecondary: darkMode ? "text-gray-400" : "text-gray-600",
    button: darkMode
      ? "bg-blue-600 hover:bg-blue-700"
      : "bg-blue-600 hover:bg-blue-700",
    buttonSecondary: darkMode
      ? "bg-gray-700 hover:bg-gray-600"
      : "bg-gray-100 hover:bg-gray-200",
  };

  // Translation function for category names
  const translateCategory = (name) => {
    const translations = {
      "Strengths": "Fortalezas",
      "Relevance": "Relevancia",
      "Clarity": "Claridad",
      "Confidence": "Confianza",
      "Content Quality": "Calidad del Contenido",
      "ContentQuality": "Calidad del Contenido",
      "Weakness": "Debilidades",
    };
    return translations[name] || name;
  };

  // Translation function for difficulty levels
  const translateDifficulty = (difficulty) => {
    const translations = {
      "Easy": "Fácil",
      "Medium": "Medio",
      "Hard": "Difícil",
    };
    return translations[difficulty] || difficulty;
  };

  // Fetch overall analytics when component mounts
  useEffect(() => {
    const fetchOverallAnalytics = async () => {
      try {
        setLoading(true);
        // Replace with your actual interview ID from Redux state
        const interviewId = interviewData?._id;

        const data = await api.fetchOverallAnalytics(interviewId);

        setOverallAnalytics(data.data.OverAllAnalytics);
      } catch (err) {
        // setError("Error al obtener el análisis: " + err.message);
        toast.error("Error al obtener el análisis: " + err.message, {
          position: toast.POSITION.TOP_CENTER,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchOverallAnalytics();
  }, [interviewData?._id]);

  const handleResetInterview = () => {
    dispatch(interviewActions.resetInterview());
  };

  const handleNewInterview = () => {
    dispatch(interviewActions.setCurrentStep("setup"));
    dispatch(interviewActions.resetInterview());
  };

  const getGradeColor = (grade) => {
    const gradeColors = {
      "A+": "text-green-600",
      A: "text-green-600",
      "A-": "text-green-600",
      "B+": "text-blue-600",
      B: "text-blue-600",
      "B-": "text-blue-600",
      "C+": "text-yellow-600",
      C: "text-yellow-600",
      "C-": "text-yellow-600",
      "D+": "text-orange-600",
      D: "text-orange-600",
      "D-": "text-orange-600",
      F: "text-red-600",
    };
    return gradeColors[grade] || "text-gray-600";
  };

  const getScoreColor = (score) => {
    if (score >= 4.0) return "text-green-600";
    if (score >= 3.0) return "text-blue-600";
    if (score >= 2.0) return "text-yellow-600";
    return "text-red-600";
  };

  const getScoreBackground = (score) => {
    if (score >= 4.0) return "bg-green-50 border-green-200";
    if (score >= 3.0) return "bg-blue-50 border-blue-200";
    if (score >= 2.0) return "bg-yellow-50 border-yellow-200";
    return "bg-red-50 border-red-200";
  };

  // Convert analytics data for frontend use
  const getAnalyticsMetrics = () => {
    if (!overallAnalytics) return [];

    const metricsOrder = [
      "strengths",
      "relevance",
      "clarity",
      "confidence",
      "contentQuality",
      "weakness",
    ];
    return metricsOrder
      .filter((key) => overallAnalytics[key])
      .map((key) => ({
        key,
        name:
          key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, " $1"),
        ...overallAnalytics[key],
      }));
  };

  const getStrengths = () => {
    return getAnalyticsMetrics()
      .filter((metric) => metric.averageScore >= 4.0)
      .sort((a, b) => b.averageScore - a.averageScore)
      .slice(0, 3);
  };

  const getImprovementAreas = () => {
    return getAnalyticsMetrics()
      .filter((metric) => metric.averageScore < 3.0)
      .sort((a, b) => a.averageScore - b.averageScore)
      .slice(0, 3);
  };

  const getDifficultyData = () => {
    if (!overallAnalytics) return [];

    const difficulties = ["easy", "medium", "hard"];
    return difficulties
      .map((difficulty) => {
        const metrics = getAnalyticsMetrics().filter(
          (metric) =>
            metric.breakdown &&
            metric.breakdown[difficulty] &&
            metric.breakdown[difficulty].count > 0
        );

        if (metrics.length === 0) return null;

        const averageScore =
          metrics.reduce(
            (sum, metric) => sum + metric.breakdown[difficulty].average,
            0
          ) / metrics.length;

        const totalQuestions = Math.max(
          ...metrics.map((metric) => metric.breakdown[difficulty].count)
        );

        return {
          difficulty: difficulty.charAt(0).toUpperCase() + difficulty.slice(1),
          averageScore: Math.round(averageScore * 20), // Convert to percentage
          count: totalQuestions,
        };
      })
      .filter(Boolean);
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen ${theme.bg} flex items-center justify-center`}
      >
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className={`${theme.text} text-lg`}>
            Calculando tus resultados...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`min-h-screen ${theme.bg} flex items-center justify-center`}
      >
        <div
          className={`${theme.card} rounded-xl p-8 border text-center max-w-md`}
        >
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className={`${theme.text} text-xl font-semibold mb-2`}>
            Error al Cargar Resultados
          </h3>
          <p className={`${theme.textSecondary} mb-4`}>{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  const analyticsMetrics = getAnalyticsMetrics();
  const strengths = getStrengths();
  const improvementAreas = getImprovementAreas();
  const difficultyData = getDifficultyData();
  const summary = overallAnalytics?.summary || {};

  const overallGrade = summary.overallGrade || "N/A";
  const overallScorePercent = Math.round((summary.overallAverage || 0) * 20); // Convert to percentage

  console.log("summary", summary);
  console.log("overallAnalytics", overallAnalytics);

  return (
    <div className={`min-h-screen ${theme.bg} transition-all duration-300`}>
      <div className="container mx-auto px-4 py-4 sm:py-8">
        <div className="max-w-6xl mx-auto">
          {/* Celebration Header */}
          <div
            className={`${theme.card} rounded-2xl shadow-2xl border p-4 sm:p-8 mb-6 sm:mb-8 text-center`}
          >
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-4 mb-4 sm:mb-6">
              <Award className="h-12 w-12 sm:h-16 sm:w-16 text-yellow-500 animate-bounce" />
              <div>
                <h2
                  className={`text-2xl sm:text-4xl font-bold ${theme.text} mb-2`}
                >
                  ¡Entrevista Completa! 🎉
                </h2>
                <p className={`${theme.textSecondary} text-lg sm:text-xl`}>
                  ¡Excelente trabajo completando las {questions.length}{" "}
                  preguntas para {interviewConfig.role}!
                </p>
              </div>
              <Sparkles className="h-12 w-12 sm:h-16 sm:w-16 text-purple-500 animate-pulse" />
            </div>
          </div>

          {/* Main Results Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 sm:gap-8">
            {/* Performance Overview */}
            <div className="xl:col-span-2 space-y-6 sm:space-y-8">
              {/* Stats Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
                <StatCard
                  title="Preguntas"
                  value={summary.totalQuestions || questions.length}
                  icon={<CheckCircle className="h-6 w-6 sm:h-8 sm:w-8" />}
                  gradient="from-blue-50 to-blue-100"
                  textColor="text-blue-600"
                  theme={theme}
                />
                <StatCard
                  title="Analizadas"
                  value={summary.analyzedQuestions || completedQuestions.length}
                  icon={<Users className="h-6 w-6 sm:h-8 sm:w-8" />}
                  gradient="from-green-50 to-green-100"
                  textColor="text-green-600"
                  theme={theme}
                />
                <StatCard
                  title="Puntuación"
                  value={`${overallScorePercent}%`}
                  icon={<Target className="h-6 w-6 sm:h-8 sm:w-8" />}
                  gradient="from-purple-50 to-purple-100"
                  textColor="text-purple-600"
                  theme={theme}
                />
                <StatCard
                  title="Calificación"
                  value={overallGrade}
                  icon={<Star className="h-6 w-6 sm:h-8 sm:w-8" />}
                  gradient="from-yellow-50 to-yellow-100"
                  textColor="text-yellow-600"
                  theme={theme}
                />
              </div>

              {/* Performance Chart */}
              <div
                className={`${theme.card} rounded-xl shadow-lg border p-4 sm:p-6`}
              >
                <h3
                  className={`text-xl sm:text-2xl font-semibold ${theme.text} mb-4 sm:mb-6 flex items-center`}
                >
                  <BarChart3 className="h-5 w-5 sm:h-6 sm:w-6 mr-2 sm:mr-3 text-blue-600" />
                  Análisis por Categoría
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {analyticsMetrics.map((metric, index) => (
                    <div
                      key={index}
                      className={`p-3 sm:p-4 border rounded-lg ${getScoreBackground(
                        metric.averageScore
                      )}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span
                          className={`font-medium text-orange-800 text-sm sm:text-base`}
                        >
                          {translateCategory(metric.name)}
                        </span>
                        <span
                          className={`font-bold text-lg ${getScoreColor(
                            metric.averageScore
                          )}`}
                        >
                          {metric.averageScore.toFixed(1)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span
                          className={`text-xs sm:text-sm ${getScoreColor(
                            metric.averageScore
                          )} font-semibold`}
                        >
                          {metric.grade}
                        </span>
                        <span className={`text-xs ${theme.textSecondary}`}>
                          /5.0
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Difficulty Performance */}
              {difficultyData.length > 0 && (
                <div
                  className={`${theme.card} rounded-xl shadow-lg border p-4 sm:p-6`}
                >
                  <h3
                    className={`text-xl font-semibold ${theme.text} mb-4 flex items-center`}
                  >
                    <TrendingUp className="h-5 w-5 mr-2 text-orange-600" />
                    Desempeño por Dificultad
                  </h3>
                  <div className="space-y-3">
                    {difficultyData.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between"
                      >
                        <div className="flex items-center flex-1">
                          <span
                            className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium mr-3 ${
                              item.difficulty === "Easy"
                                ? "bg-green-100 text-green-800"
                                : item.difficulty === "Medium"
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {translateDifficulty(item.difficulty)}
                          </span>
                          <span
                            className={`${theme.textSecondary} text-xs sm:text-sm`}
                          >
                            ({item.count} pregunta{item.count > 1 ? "s" : ""})
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-16 sm:w-20 bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${
                                item.averageScore >= 80
                                  ? "bg-green-500"
                                  : item.averageScore >= 60
                                  ? "bg-yellow-500"
                                  : "bg-red-500"
                              }`}
                              style={{
                                width: `${Math.min(item.averageScore, 100)}%`,
                              }}
                            />
                          </div>
                          <span
                            className={`font-bold text-sm sm:text-lg ${
                              item.averageScore >= 80
                                ? "text-green-600"
                                : item.averageScore >= 60
                                ? "text-yellow-600"
                                : "text-red-600"
                            }`}
                          >
                            {item.averageScore}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-4 sm:space-y-6">
              {/* Final Grade */}
              {/* <div
                className={`${theme.card} rounded-xl shadow-lg border p-4 sm:p-6 text-center`}
              >
                <h3
                  className={`font-semibold ${theme.text} mb-4 flex items-center justify-center text-sm sm:text-base`}
                >
                  <Award className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-yellow-500" />
                  Desempeño General
                </h3>
                <CircularProgress
                  percentage={overallScorePercent}
                  size={96}
                  strokeWidth={8}
                />
                <div
                  className={`text-2xl sm:text-3xl font-bold ${getGradeColor(
                    overallGrade
                  )} mb-2 mt-4`}
                >
                  {overallGrade}
                </div>
                <p
                  className={`text-base sm:text-lg font-medium ${getGradeColor(
                    overallGrade
                  )} mb-2`}
                >
                  {overallScorePercent >= 90
                    ? "¡Sobresaliente!"
                    : overallScorePercent >= 80
                    ? "¡Excelente!"
                    : overallScorePercent >= 70
                    ? "¡Bueno!"
                    : overallScorePercent >= 60
                    ? "¡Aceptable!"
                    : "¡Sigue Practicando!"}
                </p>
                <p className={`text-xs sm:text-sm ${theme.textSecondary}`}>
                  {summary.overallAverage?.toFixed(1)}/5.0 Promedio
                </p>
              </div> */}

              {/* Strengths */}
              {strengths.length > 0 && (
                <div
                  className={`${theme.card} rounded-xl shadow-lg border p-4 sm:p-6`}
                >
                  <h3
                    className={`font-semibold ${theme.text} mb-4 flex items-center text-sm sm:text-base`}
                  >
                    <Star className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-green-500" />
                    Tus Fortalezas
                  </h3>
                  <div className="space-y-2 sm:space-y-3">
                    {strengths.map((strength, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 sm:p-3 bg-green-50 rounded-lg"
                      >
                        <span className="font-medium text-green-800 text-xs sm:text-sm">
                          {translateCategory(strength.name)}
                        </span>
                        <div className="flex items-center space-x-1">
                          <span className="font-bold text-green-600 text-sm">
                            {strength.averageScore.toFixed(1)}
                          </span>
                          <span className="text-xs text-green-500">
                            ({strength.grade})
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Improvement Areas */}
              {improvementAreas.length > 0 && (
                <div
                  className={`${theme.card} rounded-xl shadow-lg border p-4 sm:p-6`}
                >
                  <h3
                    className={`font-semibold ${theme.text} mb-4 flex items-center text-sm sm:text-base`}
                  >
                    <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-orange-500" />
                    Áreas de Enfoque
                  </h3>
                  <div className="space-y-2 sm:space-y-3">
                    {improvementAreas.map((area, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 sm:p-3 bg-orange-50 rounded-lg"
                      >
                        <span className="font-medium text-orange-800 text-xs sm:text-sm">
                          {translateCategory(area.name)}
                        </span>
                        <div className="flex items-center space-x-1">
                          <span className="font-bold text-orange-600 text-sm">
                            {area.averageScore.toFixed(1)}
                          </span>
                          <span className="text-xs text-orange-500">
                            ({area.grade})
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className={`text-xs ${theme.textSecondary} mt-3`}>
                    Enfócate en estas áreas para tu próxima práctica de
                    entrevista.
                  </p>
                </div>
              )}

              {/* Interview Summary */}
              <div
                className={`${theme.card} rounded-xl shadow-lg border p-4 sm:p-6`}
              >
                <h3
                  className={`font-semibold ${theme.text} mb-4 text-sm sm:text-base`}
                >
                  Resumen de Entrevista
                </h3>
                <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm">
                  <div className="flex justify-between">
                    <span className={theme.textSecondary}>Puesto</span>
                    <span
                      className={`${theme.text} font-medium text-right max-w-32 truncate`}
                    >
                      {interviewConfig.role}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className={theme.textSecondary}>Empresa</span>
                    <span
                      className={`${theme.text} font-medium text-right max-w-32 truncate`}
                    >
                      {interviewConfig.company}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className={theme.textSecondary}>Nivel</span>
                    <span className={`${theme.text} font-medium`}>
                      {interviewConfig.level}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className={theme.textSecondary}>Dominio</span>
                    <span
                      className={`${theme.text} font-medium text-right max-w-32 truncate`}
                    >
                      {interviewConfig.domain}
                    </span>
                  </div>
                  {summary.calculatedAt && (
                    <div className="flex justify-between">
                      <span className={theme.textSecondary}>Completada</span>
                      <span className={`${theme.text} font-medium text-xs`}>
                        {new Date(summary.calculatedAt).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-8 sm:mt-12 flex flex-col sm:flex-row flex-wrap justify-center gap-3 sm:gap-4">
            <button
              onClick={handleResetInterview}
              className="flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-medium transition-all duration-200 transform hover:scale-105 shadow-lg text-sm sm:text-base"
            >
              <RefreshCw className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
              Practicar Mismo Puesto
            </button>
            <button
              onClick={handleNewInterview}
              className={`flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 border-2 border-gray-300 rounded-xl ${
                darkMode ? "" : "hover:bg-gray-50"
              }  transition-all duration-200 transform hover:scale-105 ${
                theme.text
              } text-sm sm:text-base`}
            >
              <Settings className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
              Nueva Configuración de Entrevista
            </button>
            {/* <button className="flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl font-medium transition-all duration-200 transform hover:scale-105 shadow-lg text-sm sm:text-base">
              <Share2 className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
              Compartir Resultados
            </button> */}
          </div>

          {/* Motivational Message */}
          <div className="mt-6 sm:mt-8 text-center">
            <p className={`text-base sm:text-lg ${theme.textSecondary} mb-2`}>
              "El éxito es donde la preparación y la oportunidad se encuentran."
            </p>
            <p className={`text-sm ${theme.textSecondary}`}>
              ¡Sigue practicando para mejorar tus habilidades de entrevista! 💪
            </p>
          </div>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default Sp_ResultsPage;
